import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";

const BLOCK_TYPE_LABEL = {
  PERSON: "Blocked by a person",
  PARENT: "Blocked with their LMBP/franchise",
  WRONG_PASSWORD: "Wrong passwords (system)",
};

// Lists blocked users with why they are blocked, and unblocks them after
// showing that reason. Status "3" on the user fetch returns users blocked by a
// person and users locked out by wrong passwords.
const BlockedUsersModal = ({ show, handleClose, onUnblocked }) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBlockedUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosPostApi(
        `${apiList.FETCH_USER_DATA}?pageSize=100&pageNo=1`,
        { username: loggedInUserName, status: "3" },
      );
      const rows = response?.data?.status ? response?.data?.data || [] : [];
      setUsers(rows.filter((u) => u?.blockType));
    } catch (error) {
      console.error("Error fetching blocked users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) fetchBlockedUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleUnblock = (user) => {
    CustomAlert({
      title: `Unblock ${user.userId}?`,
      text: `Reason: ${BLOCK_TYPE_LABEL[user.blockType] || user.blockType}. ${
        user.blockReason || ""
      }`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Unblock",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      const response = await axiosPostApi(apiList.UNBLOCK_USER, {
        username: loggedInUserName,
        userId: user.userId,
      });
      const ok = response?.data?.respCode === "00";
      CustomAlert({
        title: ok ? "Success" : "Unable to unblock",
        text: response?.data?.message || "Something went wrong.",
        icon: ok ? "success" : "error",
      });
      if (ok) {
        fetchBlockedUsers();
        onUnblocked && onUnblocked();
      }
    });
  };

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-[14px] shadow-lg w-[95%] max-w-[960px] max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-center px-5 py-4 border-b">
          <h5 className="text-[18px] font-medium">Blocked Users</h5>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-800 text-xl"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="overflow-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : users.length === 0 ? (
            <p className="text-sm text-gray-500">No blocked users.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FFF3EC] text-left text-[#2e237f]">
                  <th className="px-3 py-2">User ID</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Profile</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Details</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.userId} className="border-b">
                    <td className="px-3 py-2 whitespace-nowrap">{u.userId}</td>
                    <td className="px-3 py-2">{u.name}</td>
                    <td className="px-3 py-2">{u.userProfile}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {BLOCK_TYPE_LABEL[u.blockType] || u.blockType}
                    </td>
                    <td className="px-3 py-2">{u.blockReason}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="btn btn-submit !rounded-lg !py-1 !px-3"
                        onClick={() => handleUnblock(u)}
                      >
                        Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BlockedUsersModal;
